/**
 * Shared skeleton set for known-layout loading surfaces.
 *
 * Bones use `bg-muted` and a 4px radius. Pulse is optional and off when the
 * user prefers reduced motion. No shimmer, no invented colours.
 */
import React from 'react';
import { clsx } from 'clsx';

const BONE = 'bg-muted rounded-md';
const PULSE = 'animate-pulse motion-reduce:animate-none';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Subtle pulse. Off when the user prefers reduced motion. Default true. */
    pulse?: boolean;
}

/** Single bone. Size it with Tailwind width/height classes. */
export const Skeleton: React.FC<SkeletonProps> = ({
    className,
    pulse = true,
    ...props
}) => (
    <div
        data-testid="skeleton"
        className={clsx(BONE, pulse && PULSE, className)}
        {...props}
    />
);

export interface SkeletonRowProps {
    className?: string;
    /** Number of bones in the row. Default 2 (label + value). */
    bones?: number;
    pulse?: boolean;
}

/** One inspector-style row: a short label bone plus a longer value bone. */
export const SkeletonRow: React.FC<SkeletonRowProps> = ({
    className,
    bones = 2,
    pulse = true,
}) => (
    <div
        className={clsx('flex items-center gap-3', className)}
        data-testid="skeleton-row"
    >
        {Array.from({ length: bones }, (_, i) => (
            <Skeleton
                key={i}
                pulse={pulse}
                className={clsx('h-4', i === 0 ? 'w-1/4' : 'flex-1')}
            />
        ))}
    </div>
);

export interface SkeletonStatProps {
    className?: string;
    pulse?: boolean;
}

/** Stat cell: small caption bone over a larger figure bone. */
export const SkeletonStat: React.FC<SkeletonStatProps> = ({
    className,
    pulse = true,
}) => (
    <div className={clsx('space-y-2', className)} data-testid="skeleton-stat">
        <Skeleton pulse={pulse} className="h-3 w-16" />
        <Skeleton pulse={pulse} className="h-6 w-12" />
    </div>
);

export interface SkeletonTableProps {
    className?: string;
    rows?: number;
    columns?: number;
    /** Accessible name for the status region. */
    label?: string;
    pulse?: boolean;
}

/** Table-shaped placeholder. Default 6-column grid matches the entity registry. */
export const SkeletonTable: React.FC<SkeletonTableProps> = ({
    className,
    rows = 7,
    columns = 6,
    label = 'Loading',
    pulse = true,
}) => (
    <div
        className={clsx('divide-y divide-border/60', className)}
        role="status"
        aria-label={label}
        data-testid="skeleton-table"
    >
        {Array.from({ length: rows }, (_, row) => (
            <div
                key={row}
                className={clsx(
                    'gap-4 px-4 py-3',
                    columns === 6
                        ? 'grid grid-cols-[2fr_1fr_1fr_1fr_1.3fr_1.4fr]'
                        : 'grid',
                )}
                style={columns === 6 ? undefined : { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
            >
                {Array.from({ length: columns }, (_, cell) => (
                    <Skeleton
                        key={cell}
                        pulse={pulse}
                        className={clsx('h-4', cell === 0 ? 'w-4/5' : 'w-2/3')}
                    />
                ))}
            </div>
        ))}
        <span className="sr-only">{label}</span>
    </div>
);

export interface SkeletonListProps {
    className?: string;
    count?: number;
    pulse?: boolean;
    label?: string;
}

/** Stacked rows for transfer lists, accordion bodies, and similar. */
export const SkeletonList: React.FC<SkeletonListProps> = ({
    className,
    count = 4,
    pulse = true,
    label = 'Loading',
}) => (
    <div
        className={clsx('space-y-2', className)}
        role="status"
        aria-label={label}
        data-testid="skeleton-list"
    >
        {Array.from({ length: count }, (_, i) => (
            <SkeletonRow key={i} pulse={pulse} />
        ))}
        <span className="sr-only">{label}</span>
    </div>
);
